import * as React from "react";
import config from "../../config/config";
import useOverviewData from "../../hooks/useOverviewData";
import Overview from "../../components/OverviewTable/Overview";
import OverviewTable from "../../components/OverviewTable/OverviewTable";
import t from "../../hooks/useTranslation";
import useWebSocket, {ReadyState} from 'react-use-websocket';
import moment from "moment";
import {useEffect, useState} from "react";
import AuthService from "../../AuthService";
import usePageVisibility from "../../hooks/usePageVisibility";
import sound from "../../assets/audio/1.wav";

import './AllEvents.css';
import {generateUniqueID} from "web-vitals/dist/modules/lib/generateUniqueID";

const authService = new AuthService();

function AllEvents() {
    const [count, setCount] = useState(0);
    const visibilityStatus = usePageVisibility();
    // const {
    //     isLoading,
    //     error,
    //     response,
    //     totalItemsCount,
    //     setFilter,
    //     filter,
    //     filterValues,
    // } = useOverviewData(config.apiRouteMap.alarm, 20, INITIAL_BRICKS_DATA);
    const [filter, setFilter] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState();
    const [isLoading, setIsLoading] = useState();
    const [shelfMap, setShelfMap] = useState();
    const [overviewList, setOverviewList] = useState([]);
    const [shelfList, setShelfList] = useState([]);
    const [initialMessageSent, setInitialMessageSent] = useState(false);
    const {readyState, sendJsonMessage} = useWebSocket(config.getWebSocketAddress(authService.getToken()), {
        onClose: () => {
            setInitialMessageSent(false);
        },
        shouldReconnect: () => true,
        onMessage: message => processMessage(message)
    });

    useEffect(() => {
        const init = async () => {
            const {data} = await authService.fetch(`/api/shelf/list`);
            setShelfList(data);
            setShelfMap(data.reduce((acc, shelf) => {
                acc[shelf._id] = shelf.name;
                return acc;
            }))
        };
        init();
    }, []);

    useEffect(() => {
        if (!visibilityStatus && document.previousTitle) {
            document.title = document.previousTitle;
            document.previoustitle = undefined;
            setCount(0);
        }
    }, [visibilityStatus]);

    const processMessage = (message) => {
        message = JSON.parse(message.data);
        if (message.entity === config.wssConfig.entityTypes.EVENT && message.messageType === config.wssConfig.messageTypes.NEW) {
            const newCount = count + 1;
            setCount(newCount);
            if (visibilityStatus) {
                if (!document.previousTitle) {
                    document.previousTitle = document.title;
                }
                document.title = t('newAlarm', {count: newCount});
            }
            // concat new alarm log to overviewList
            message.data.new = true;
            message.data._id = generateUniqueID();
            const newList = [message.data, ...overviewList].filter((_, i) => i < 20);
            setOverviewList(newList);
            setTotalCount(totalCount + 1);
            try {
                new Audio(sound).play();
            } catch (error) {
                console.warn("Nepodařilo se přehrát zvuk: ", error);
            }
        }
    }

    useEffect(() => {
        if (!initialMessageSent && readyState === ReadyState.OPEN && !isLoading && !error) {
            const last = overviewList?.[0]?.createdAt;
            sendJsonMessage({last});
            setInitialMessageSent(true);
        }
    }, [readyState, isLoading, error, initialMessageSent]);

    const connectionStatus = {
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Open',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
    }[readyState];

    const handleFilterChange = (key, value, type) => {
        let newFilter = {...filter};
        newFilter[key] = {type, value};
        if (
            (type === "text" && value === "")
            || (type === "select" && value === undefined)
        ) {
            delete newFilter[key];
        }
        setFilter(newFilter);
    };

    const getShelfOptions = () => {
        if (shelfList) {
            return shelfList.map(({name, serialNumber, _id}) => ({
                label: name || serialNumber,
                value: _id
            }));
        }
        return [];
    }

    const columns = [
        {
            key: 'shelf',
            title: t('shelfName'),
            // valueRenderer: row => row.shelf,
            linkRenderer: ({shelf}) => ({
                route: config.routeMap.getShelfDetail(shelf),
                label: shelfMap[shelf]
            }),
            filter: {
                type: 'select',
                filterState: filter,
                emptyOption: true,
                onChange: handleFilterChange,
                label: t('shelf'),
                options: getShelfOptions()
            }
        },
        {
            key: 'shelfNum',
            title: t('shelfSectorNumber'),
            valueRenderer: ({data}) => data.shelfNum
        },
        {
            key: 'product',
            title: t('item'),
            valueRenderer: item => `${item.product ? `${item.product} (${item.multiCount || item.count})` : ''} ${item.data.weightChange}g`,
            // filter: {
            //     type: 'text',
            //     filterState: filter,
            //     onChange: handleFilterChange,
            //     label: t('product')
            // }
        },
        {
            key: 'distanceFromLeft',
            title: t('shelfSector'),
            valueRenderer: ({data}) => data.distanceFromLeft
        },
        {
            key: 'createdAt',
            title: t('createdAt'),
            valueRenderer: ({createdAt}) => moment(createdAt).format('DD.MM.YYYY HH:mm'),
            // filter: {
            //     type: 'dateRange',
            //     filterState: filter,
            //     onChange: handleFilterChange,
            //     label: t('alarmDate')
            // }
        }
    ];

    const rowClassNameRenderer = (row) => {
        if (row.new) return 'new';
    }

    const formatNumber = (number) => {
        const reversedStr = number.toString().split('').reverse().join('');
        const chunks = reversedStr.match(/.{1,3}/g);
        return chunks.map(chunk => chunk.split('').reverse().join('')).reverse().join(' ');
    }

    const formattedNumber = formatNumber(overviewList.length);

    return (
        <div className="Alarms app__main">
            <Overview
                isLoading={isLoading}
                introduction={t('introductionText')}
                title={t('alarmsAdministration')}
                error={error}
                wsStatus={connectionStatus}
            >
                <OverviewTable title={<><strong>{t('items').toUpperCase()}</strong> ({formattedNumber})</>}
                               columns={columns} rows={overviewList}
                               resetFilter={() => setFilter({})} filter={filter}
                               isLoading={isLoading} error={error}
                               rowClassNameRenderer={rowClassNameRenderer}/>
            </Overview>
        </div>
    );
}

export default AllEvents;
